/**
 * PATH: apps/quote/src/components/builder/BuilderRightDrawer.tsx
 * PURPOSE: Unified right drawer shell with top-level tabs
 *
 * WHY:
 * - Single cohesive right panel instead of multiple overlapping drawers
 * - Tabs for Inspector, Context (Files/Emails/Drawings), AI, Claims, History
 * - Remembers tab + width preferences
 *
 * FLOW:
 *   Tab click → Switch panel content → Persist preference
 */

import { useState, useEffect } from 'react'
import {
  X,
  Pin,
  PinOff,
  Settings2,
  FolderOpen,
  Bot,
  Receipt,
  History,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Zap,
  Camera,
  BarChart3,
  Clock,
  Package,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkspace, type RightDrawerTab } from './BuilderWorkspace'
import type { QuoteLineItem, QuoteDetail } from '@/lib/quoteApi'

// Panel components (will be refactored versions of existing drawers)
import RowInspectorPanel from './panels/RowInspectorPanel'
import ContextPanel from './panels/ContextPanel'
import AIAssistantPanel from './panels/AIAssistantPanel'
import ClaimsPanel from './panels/ClaimsPanel'
import HistoryPanel from './panels/HistoryPanel'
import SnapshotsPanel from './panels/SnapshotsPanel'
import TakeoffAssistPanel from './panels/TakeoffAssistPanel'
import KPISummaryPanel from './panels/KPISummaryPanel'
import WagesBuildUpPanel from './panels/WagesBuildUpPanel'
import ProductSummaryPanel from './panels/ProductSummaryPanel'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface BuilderRightDrawerProps {
  quoteId: string
  quote: QuoteDetail | null
  selectedItem: QuoteLineItem | null
  selectedSectionId: string | null
  onItemUpdated?: (item: QuoteLineItem) => void
  onUpdateLineItem?: (sectionId: string, itemId: string, patch: Record<string, unknown>) => void
  onApplyAISuggestion?: (suggestion: Record<string, unknown>) => void
  onApplyApplication?: (sectionId: string, itemId: string, applicationId: string) => void
  onJumpToRow?: (sectionId: string, itemId: string) => void
  onAddRowWithApplication?: (sectionId: string, applicationId: string) => void
}

interface TabConfig {
  id: RightDrawerTab
  label: string
  icon: React.ComponentType<{ className?: string }>
  shortLabel?: string
}

const TABS: TabConfig[] = [
  { id: 'inspector', label: 'Inspector', shortLabel: 'Insp', icon: Settings2 },
  { id: 'takeoff', label: 'Takeoff', icon: Zap },
  // KPI sits next to Takeoff because both are "looking at the whole
  // quote" tabs — Inspector deals with the selected row, KPI deals
  // with the rolled-up quote.
  { id: 'kpi', label: 'KPI', icon: BarChart3 },
  // Wages + Products mirror the Excel "Wages Build Up" and "Product
  // Summary" worksheets; placed next to KPI so the entire roll-up
  // story (margins → labour build-up → material take-off) lives in
  // one cluster.
  { id: 'wages', label: 'Wages', icon: Clock },
  { id: 'products', label: 'Products', shortLabel: 'Prod', icon: Package },
  { id: 'context', label: 'Context', shortLabel: 'Ctx', icon: FolderOpen },
  { id: 'ai', label: 'AI', icon: Bot },
  { id: 'claims', label: 'Claims', icon: Receipt },
  { id: 'snapshots', label: 'Snapshots', shortLabel: 'Snap', icon: Camera },
  { id: 'history', label: 'History', shortLabel: 'Hist', icon: History },
]

// -----------------------------------------------------------------------------
// Tab Button
// -----------------------------------------------------------------------------

function TabButton({
  tab,
  isActive,
  onClick,
}: {
  tab: TabConfig
  isActive: boolean
  onClick: () => void
}) {
  const Icon = tab.icon

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
        isActive
          ? 'bg-blue-600 text-white'
          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
      )}
      title={tab.label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden xl:inline">{tab.shortLabel || tab.label}</span>
    </button>
  )
}

// -----------------------------------------------------------------------------
// Resize Handle
// -----------------------------------------------------------------------------

function ResizeHandle({ onResize }: { onResize: (delta: number) => void }) {
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      // Negative delta because dragging left should increase width
      onResize(-e.movementX)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, onResize])

  return (
    <div
      className={cn(
        'absolute left-0 top-0 bottom-0 w-1 cursor-col-resize group',
        'hover:bg-blue-400 transition-colors',
        isDragging && 'bg-blue-500'
      )}
      onMouseDown={() => setIsDragging(true)}
    >
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export default function BuilderRightDrawer({
  quoteId,
  quote,
  selectedItem,
  selectedSectionId,
  onItemUpdated,
  onUpdateLineItem,
  onApplyAISuggestion,
  onApplyApplication,
  onJumpToRow,
  onAddRowWithApplication,
}: BuilderRightDrawerProps) {
  const {
    rightDrawerTab,
    setRightDrawerTab,
    rightDrawerPinned,
    toggleRightDrawerPin,
    closeRightDrawer,
    rightDrawerWidth,
    setRightDrawerWidth,
  } = useWorkspace()

  const handleResize = (delta: number) => {
    setRightDrawerWidth(rightDrawerWidth + delta)
  }

  return (
    <div className="h-full min-h-0 flex flex-col relative" style={{ width: rightDrawerWidth }}>
      {/* Resize handle */}
      <ResizeHandle onResize={handleResize} />

      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={rightDrawerTab === tab.id}
              onClick={() => setRightDrawerTab(tab.id)}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={toggleRightDrawerPin}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              rightDrawerPinned
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            )}
            title={rightDrawerPinned ? 'Unpin drawer' : 'Pin drawer open'}
          >
            {rightDrawerPinned ? (
              <Pin className="w-4 h-4" />
            ) : (
              <PinOff className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={closeRightDrawer}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Close drawer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content - scrollable panel area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {rightDrawerTab === 'inspector' && (
          <RowInspectorPanel
            quoteId={quoteId}
            quote={quote}
            sectionId={selectedSectionId || ''}
            lineItem={selectedItem}
            onLineItemUpdated={onItemUpdated}
            onUpdateLineItem={onUpdateLineItem}
          />
        )}
        {rightDrawerTab === 'takeoff' && (
          <TakeoffAssistPanel
            quoteId={quoteId}
            sections={quote?.sections || []}
            selectedSectionId={selectedSectionId}
            selectedItemId={selectedItem?.id || null}
            onApplyApplication={onApplyApplication || (() => {})}
            onJumpToRow={onJumpToRow || (() => {})}
            onAddRow={onAddRowWithApplication || (() => {})}
          />
        )}
        {rightDrawerTab === 'kpi' && <KPISummaryPanel quote={quote} />}
        {rightDrawerTab === 'wages' && <WagesBuildUpPanel quote={quote} />}
        {rightDrawerTab === 'products' && (
          <ProductSummaryPanel quote={quote} />
        )}
        {rightDrawerTab === 'context' && (
          <ContextPanel
            quoteId={quoteId}
            quote={quote}
          />
        )}
        {rightDrawerTab === 'ai' && (
          <AIAssistantPanel
            quoteId={quoteId}
            quote={quote}
            onApplySuggestion={onApplyAISuggestion}
          />
        )}
        {rightDrawerTab === 'claims' && (
          <ClaimsPanel
            quoteId={quoteId}
            quote={quote}
          />
        )}
        {rightDrawerTab === 'snapshots' && (
          <SnapshotsPanel
            quoteId={quoteId}
          />
        )}
        {rightDrawerTab === 'history' && (
          <HistoryPanel
            quoteId={quoteId}
          />
        )}
      </div>
    </div>
  )
}

